import { isSymbolBytes } from './model';
import type { ProjectModelDraft } from '../project/model';
import type { PointSymbol, VectorLayerStyle } from '../styles/model';
import {
  equalBytes,
  isAssetGraphic,
  SYMBOL_LIMITS,
  SymbolImportError,
  symbolName,
  type SymbolAsset,
} from './model';

export function pointSymbolDefinitions(style: VectorLayerStyle): readonly Partial<PointSymbol>[] {
  const c = style.classification;
  const thematic =
    c.mode === 'categorised'
      ? [...c.categories.map((e) => e.symbol), c.other]
      : c.mode === 'graduated'
        ? [...c.classes.map((e) => e.symbol), c.nullSymbol]
        : [];
  return [
    style.symbols.point,
    ...thematic.flatMap((e) => (e.override?.point ? [e.override.point] : [])),
  ];
}
export function symbolUsage(
  project: Pick<ProjectModelDraft, 'layers'> & Partial<Pick<ProjectModelDraft, 'annotationLayers'>>,
  id: string,
): readonly string[] {
  return [
    ...project.layers.flatMap((layer) =>
      pointSymbolDefinitions(layer.style)
        .filter((point) => isAssetGraphic(point.shape) && point.shape.assetId === id)
        .map(() => layer.name),
    ),
    ...(project.annotationLayers ?? []).flatMap((layer) =>
      layer.annotations.flatMap((a) =>
        a.type === 'marker' && isAssetGraphic(a.style.shape) && a.style.shape.assetId === id
          ? [`${layer.name} / ${a.name}`]
          : [],
      ),
    ),
  ];
}
export function validateSymbolReferences(
  project: Pick<ProjectModelDraft, 'layers' | 'symbolAssets'> &
    Partial<Pick<ProjectModelDraft, 'annotationLayers'>>,
): void {
  const ids = new Set(project.symbolAssets.map((a) => a.id));
  for (const layer of project.layers)
    for (const point of pointSymbolDefinitions(layer.style)) {
      if (isAssetGraphic(point.shape) && !ids.has(point.shape.assetId))
        throw new SymbolImportError('A point style references a missing symbol.');
    }
  for (const layer of project.annotationLayers ?? [])
    for (const a of layer.annotations)
      if (a.type === 'marker' && isAssetGraphic(a.style.shape) && !ids.has(a.style.shape.assetId))
        throw new SymbolImportError('An annotation marker references a missing symbol.');
}
export function validateRegistry(assets: readonly SymbolAsset[]): void {
  if (
    assets.length > SYMBOL_LIMITS.assets ||
    assets.reduce((n, a) => n + a.bytes.byteLength, 0) > SYMBOL_LIMITS.totalBytes
  )
    throw new SymbolImportError(
      'The symbol library exceeds 64 symbols or 32 MiB. Remove an unused symbol first.',
    );
  const ids = new Set<string>();
  for (const asset of assets) {
    if (
      !/^sym_[A-Za-z0-9_-]{1,80}$/.test(asset.id) ||
      ids.has(asset.id) ||
      symbolName(asset.name) !== asset.name ||
      !/^[0-9a-f]{64}$/.test(asset.sha256) ||
      !['image/png', 'image/svg+xml'].includes(asset.mimeType) ||
      !isSymbolBytes(asset.bytes) ||
      !asset.bytes.length ||
      asset.bytes.length >
        (asset.mimeType === 'image/png' ? SYMBOL_LIMITS.pngBytes : SYMBOL_LIMITS.svgBytes) ||
      !Number.isFinite(asset.width) ||
      !Number.isFinite(asset.height) ||
      asset.width <= 0 ||
      asset.height <= 0 ||
      asset.width / asset.height < 0.1 ||
      asset.width / asset.height > 10
    )
      throw new SymbolImportError('The project contains an invalid symbol descriptor.');
    ids.add(asset.id);
  }
}
export function addSymbolAsset(
  assets: readonly SymbolAsset[],
  asset: SymbolAsset,
): { assets: readonly SymbolAsset[]; asset: SymbolAsset; duplicate: boolean } {
  const existing = assets.find(
    (a) =>
      a.mimeType === asset.mimeType &&
      a.sha256 === asset.sha256 &&
      equalBytes(a.bytes, asset.bytes),
  );
  if (existing) return { assets, asset: existing, duplicate: true };
  const next = [...assets, asset];
  validateRegistry(next);
  return { assets: next, asset, duplicate: false };
}
